<template>
  <div class="network-log-list-wrapper">
    <LogSearch v-model="networkSearch" />
    <LogFilters v-model="networkFilterValue" :options="filterOptions" />

    <div ref="containerRef" class="network-log-list" data-testid="logs-network-list">
      <button
        v-for="entry in entries"
        :key="entry.id"
        type="button"
        class="network-entry"
        :class="{ 'network-entry--active': entry.id === activeEntryId }"
        :data-log-id="entry.id"
        :data-testid="`network-entry-${entry.id}`"
        @click="onEntryClick(entry)"
      >
        <span class="network-entry__status" :class="statusClass(entry.status)">{{ entry.status }}</span>
        <span class="network-entry__method">{{ entry.method }}</span>
        <span class="network-entry__timestamp">{{ formatLogTimestamp(entry.timestamp) }}</span>
        <span class="network-entry__url" :title="entry.url">{{ entry.url }}</span>
        <span class="network-entry__duration">{{ entry.duration }}ms</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import LogSearch from './LogSearch.vue';
import LogFilters, { type FilterOption } from './LogFilters.vue';
import { storeToRefs } from 'pinia';
import { formatLogTimestamp } from '@/modules/viewer/composables/useLogs';
import { useLogsStore } from '@/modules/viewer/store/useLogsStore';
import { useActiveLogEntry } from '@/modules/viewer/composables/useActiveLogEntry';
import { useTrack, TrackEvents } from '@/composables/useTrack';
import type { NetworkEntry, NetworkFilter } from '@/modules/viewer/types/logs';

const props = defineProps<{
  autoScroll: boolean;
}>();

const logsStore = useLogsStore();
const { filteredNetworkEntries, networkFilter, networkSearch, networkCounts } = storeToRefs(logsStore);
const { selectNetworkEntry } = logsStore;
const entries = computed(() => filteredNetworkEntries.value);

const { track } = useTrack();

const onEntryClick = (entry: NetworkEntry) => {
  selectNetworkEntry(entry.id);
  track(TrackEvents.LOGS_NETWORK_ENTRY_CLICKED, {
    status: entry.status,
    method: entry.method,
    type: entry.type,
  });
};

const networkFilterValue = computed<NetworkFilter>({
  get: () => networkFilter.value,
  set: value => {
    networkFilter.value = value as NetworkFilter;
  },
});

const filterOptions = computed<FilterOption[]>(() => [
  { value: 'all', label: 'All', count: networkCounts.value.all },
  { value: 'xhr', label: 'XHR / Fetch', count: networkCounts.value.xhr },
  { value: 'asset', label: 'Assets', count: networkCounts.value.asset },
]);

const { containerRef, activeEntryId } = useActiveLogEntry(entries, toRef(props, 'autoScroll'));

const statusClass = (status: number): string => {
  if (status >= 500) return 'network-entry__status--error';
  if (status >= 400) return 'network-entry__status--warn';
  if (status >= 300) return 'network-entry__status--redirect';
  return 'network-entry__status--success';
};
</script>

<style scoped>
.network-log-list-wrapper {
  @apply flex flex-col;
}

.network-log-list {
  @apply flex flex-col;
}

.network-entry {
  @apply grid items-center gap-3 w-full px-4 py-2 text-left cursor-pointer transition-colors border-b border-zinc-100 dark:border-zinc-700/60;
  @apply hover:bg-zinc-50 dark:hover:bg-zinc-700/50;
  grid-template-columns: auto auto auto 1fr auto;
}

.network-entry--active {
  @apply bg-blue-50 dark:bg-blue-900/20;
}

.network-entry--active:hover {
  @apply bg-blue-100 dark:bg-blue-900/30;
}

.network-entry__status {
  @apply inline-flex items-center justify-center min-w-[40px] px-1.5 py-0.5 text-xs font-semibold rounded;
}

.network-entry__status--success {
  @apply bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400;
}

.network-entry__status--redirect {
  @apply bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400;
}

.network-entry__status--warn {
  @apply bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400;
}

.network-entry__status--error {
  @apply bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400;
}

.network-entry__method {
  @apply text-xs font-mono font-semibold text-zinc-600 dark:text-zinc-400;
}

.network-entry__timestamp {
  @apply font-mono text-xs text-blue-600 dark:text-blue-400;
}

.network-entry__url {
  @apply text-sm text-zinc-800 dark:text-zinc-200 truncate min-w-0;
}

.network-entry__duration {
  @apply text-xs font-mono text-zinc-500 dark:text-zinc-400;
}
</style>
